<script>
import moment from "moment";
import { getTechActivityDetail } from "@/api/techInnoActivities";

const emptyForm = () => ({
  // id（编辑时使用）
  id: "",
  // 板块id
  sectionId: undefined,
  // 活动类型id
  typeId: undefined,
  // 名称
  name: "",
  // 活动时间（开始、结束）
  activityTime: [],
  // 地点
  place: "",
  // 规模人数（报名人数上限）
  capacity: undefined,
  // 简介
  introduction: "",
  // 日程条目（时间段 + 主题 + 内容）
  agenda: [],
  // 是否审核
  needAudit: 1,
  // 是否签到
  needCheckin: 1,
  // 是否开放报名（默认开放）
  needRegistration: 1,
  // 报名起止时间（可空=不限；配置任一端后用户端报名只按窗口校验）
  signupBeginTime: null,
  signupEndTime: null,
  // 邀约人员手机号（非必填、无上限；接口为逗号分隔字符串 invitePhones）
  invitePhoneList: [],
  // 核销人手机号
  verifierPhones: [],
});

export default {
  name: "ActivityForm",
  props: {
    // 弹窗显隐
    visible: {
      type: Boolean,
      default: false,
    },
    // 操作类型 add 新增 / edit 编辑 / view 查看
    mode: {
      type: String,
      default: "add",
    },
    // 当前操作的活动数据
    activityData: {
      type: Object,
      default: () => ({}),
    },
    // 板块字典
    sectionList: {
      type: Array,
      default: () => [],
    },
    // 活动类型字典
    activityTypeList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      moment,
      labelCol: { span: 4 },
      wrapperCol: { span: 20 },
      form: emptyForm(),
      confirmLoading: false,
      detailLoading: false,
      rules: {
        sectionId: [{ required: true, message: "请选择板块", trigger: "change" }],
        typeId: [{ required: true, message: "请选择活动类型", trigger: "change" }],
        name: [{ required: true, message: "请输入名称", trigger: "blur" }],
        activityTime: [{ required: true, message: "请选择活动时间", trigger: "change" }],
        place: [{ required: true, message: "请输入地点", trigger: "blur" }],
        capacity: [{ required: true, message: "请输入规模人数", trigger: "blur" }],
        verifierPhones: [
          { required: true, type: "array", message: "请至少添加一名核销人", trigger: "change" },
        ],
      },
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    },
    modalTitle() {
      const titleMap = {
        add: "新增活动",
        edit: "编辑活动",
        view: "查看活动",
      };
      return titleMap[this.mode] || "活动";
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.form = emptyForm();
        if (this.mode !== "add") {
          this.initFormData();
        }
      }
    },
  },
  methods: {
    /**
     * @Description：编辑/查看时初始化表单数据，优先取详情接口
     * @Author cxd
     * @Date 2026/10/26
     */
    async initFormData() {
      const row = this.activityData || {};
      this.form = this.mapRowToForm(row);
      if (!row.id) return;
      this.detailLoading = true;
      try {
        const res = await getTechActivityDetail(row.id);
        if (String(res.code) === "200" && res.data) {
          this.form = this.mapRowToForm(res.data);
        }
      } catch (e) {
        // 详情接口异常时使用列表行数据兜底
      } finally {
        this.detailLoading = false;
      }
    },
    /**
     * @Description：接口数据 -> 表单数据
     * @Author cxd
     * @Date 2026/10/26
     */
    mapRowToForm(row) {
      const agenda = Array.isArray(row.agendaItems)
        ? row.agendaItems.map((item) => ({
            startTime: item.startTime ? moment(item.startTime, "HH:mm") : null,
            endTime: item.endTime ? moment(item.endTime, "HH:mm") : null,
            theme: item.theme || "",
            content: item.content || "",
          }))
        : [];
      return {
        id: row.id != null ? row.id : "",
        sectionId: row.sectionId != null && row.sectionId !== "" ? row.sectionId : undefined,
        typeId: row.typeId != null && row.typeId !== "" ? row.typeId : undefined,
        name: row.name || "",
        activityTime:
          row.beginTime && row.endTime ? [moment(row.beginTime), moment(row.endTime)] : [],
        place: row.place || "",
        capacity: row.capacity != null && row.capacity !== "" ? Number(row.capacity) : undefined,
        introduction: row.introduction || "",
        agenda,
        needAudit: row.needAudit != null ? row.needAudit : 1,
        needCheckin: row.needCheckin != null ? row.needCheckin : 1,
        needRegistration: row.needRegistration != null ? row.needRegistration : 1,
        signupBeginTime: row.signupBeginTime ? moment(row.signupBeginTime) : null,
        signupEndTime: row.signupEndTime ? moment(row.signupEndTime) : null,
        invitePhoneList: row.invitePhones
          ? String(row.invitePhones)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        verifierPhones: Array.isArray(row.verifierPhones) ? row.verifierPhones.slice() : [],
      };
    },
    /**
     * @Description：添加日程行
     * @Author cxd
     * @Date 2026/10/26
     */
    addAgenda() {
      this.form.agenda.push({ startTime: null, endTime: null, theme: "", content: "" });
    },
    /**
     * @Description：删除日程行
     * @Author cxd
     * @Date 2026/10/26
     */
    removeAgenda(index) {
      this.form.agenda.splice(index, 1);
    },
    /**
     * @Description：添加邀约人行
     * @Author cxd
     * @Date 2026/11/1
     */
    addInvitePhone() {
      this.form.invitePhoneList.push("");
    },
    /**
     * @Description：删除邀约人行
     * @Author cxd
     * @Date 2026/11/1
     */
    removeInvitePhone(index) {
      this.form.invitePhoneList.splice(index, 1);
    },
    /**
     * @Description：添加核销人行
     * @Author cxd
     * @Date 2026/10/26
     */
    addVerifier() {
      this.form.verifierPhones.push("");
    },
    /**
     * @Description：删除核销人行
     * @Author cxd
     * @Date 2026/10/26
     */
    removeVerifier(index) {
      this.form.verifierPhones.splice(index, 1);
    },
    /**
     * @Description：提交前校验日程、核销人明细
     * @Author cxd
     * @Date 2026/10/26
     */
    validateDetail() {
      for (let i = 0; i < this.form.agenda.length; i++) {
        const item = this.form.agenda[i];
        if (!item.startTime || !item.endTime || !item.theme) {
          this.$message.warning(`请完善第 ${i + 1} 条议程的时间与主题`);
          return false;
        }
        if (item.endTime.isSameOrBefore(item.startTime)) {
          this.$message.warning(`第 ${i + 1} 条议程的结束时间需晚于开始时间`);
          return false;
        }
      }
      // 报名起止时间：都填时开始须早于结束
      if (this.form.signupBeginTime && this.form.signupEndTime) {
        if (!this.form.signupEndTime.isAfter(this.form.signupBeginTime)) {
          this.$message.warning("报名结束时间需晚于报名开始时间");
          return false;
        }
      }
      // 邀约人员（非必填、无上限）：填了则逐个校验手机号格式
      const invitePhones = this.form.invitePhoneList
        .map((p) => String(p || "").trim())
        .filter(Boolean);
      for (let i = 0; i < invitePhones.length; i++) {
        if (!/^1[3-9]\d{9}$/.test(invitePhones[i])) {
          this.$message.warning(`第 ${i + 1} 名邀请人员手机号格式不正确`);
          return false;
        }
      }
      // 核销人仅在开启签到时校验并提交
      if (this.form.needCheckin === 1) {
        const phones = this.form.verifierPhones
          .map((p) => String(p || "").trim())
          .filter(Boolean);
        for (let i = 0; i < phones.length; i++) {
          if (!/^1[3-9]\d{9}$/.test(phones[i])) {
            this.$message.warning(`第 ${i + 1} 名核销人手机号格式不正确`);
            return false;
          }
        }
        if (new Set(phones).size !== phones.length) {
          this.$message.warning("核销人手机号存在重复，请检查");
          return false;
        }
        if (!phones.length) {
          this.$message.warning("开启签到后请至少添加一名核销人");
          return false;
        }
      }
      return true;
    },
    /**
     * @Description：弹窗确认
     * @Author cxd
     * @Date 2026/10/26
     */
    handleOk() {
      this.$refs.ruleForm.validate((valid) => {
        if (!valid) return;
        if (!this.validateDetail()) return;
        this.$emit("submit", this.buildSubmitParams());
      });
    },
    /**
     * @Description：整理提交参数（新增/编辑，日程与核销人全量提交）
     * @Author cxd
     * @Date 2026/10/26
     */
    buildSubmitParams() {
      const [beginTime, endTime] = this.form.activityTime;
      const phones =
        this.form.needCheckin === 1
          ? this.form.verifierPhones.map((p) => String(p || "").trim()).filter(Boolean)
          : [];
      return {
        id: this.form.id,
        name: this.form.name,
        sectionId: this.form.sectionId,
        typeId: this.form.typeId,
        beginTime: beginTime ? moment(beginTime).format("YYYY-MM-DD HH:mm:ss") : "",
        endTime: endTime ? moment(endTime).format("YYYY-MM-DD HH:mm:ss") : "",
        place: this.form.place,
        capacity: this.form.capacity,
        introduction: this.form.introduction,
        needAudit: this.form.needAudit,
        needCheckin: this.form.needCheckin,
        needRegistration: this.form.needRegistration,
        // 报名起止时间：空传 null（不限），编辑时也可清空；不开放报名时不提交窗口（避免脏数据）
        signupBeginTime:
          this.form.needRegistration === 1 && this.form.signupBeginTime
            ? moment(this.form.signupBeginTime).format("YYYY-MM-DD HH:mm:ss")
            : null,
        signupEndTime:
          this.form.needRegistration === 1 && this.form.signupEndTime
            ? moment(this.form.signupEndTime).format("YYYY-MM-DD HH:mm:ss")
            : null,
        // 邀约人员：逗号分隔字符串，空=未配置；不开放报名时不提交（避免脏数据）
        invitePhones:
          this.form.needRegistration === 1
            ? this.form.invitePhoneList
                .map((p) => String(p || "").trim())
                .filter(Boolean)
                .join(",")
            : "",
        agendaItems: this.form.agenda.map((item, index) => ({
          startTime: moment(item.startTime).format("HH:mm"),
          endTime: moment(item.endTime).format("HH:mm"),
          theme: item.theme,
          content: item.content,
          sort: index + 1,
        })),
        verifierPhones: Array.from(new Set(phones)),
      };
    },
    handleCancel() {
      this.$emit("close");
    },
  },
};
</script>

<template>
  <a-modal
    :title="modalTitle"
    :visible="visible"
    :confirm-loading="confirmLoading"
    :footer="isView ? null : undefined"
    destroyOnClose
    width="960px"
    :maskClosable="false"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <a-spin :spinning="detailLoading">
      <a-form-model
        ref="ruleForm"
        :model="form"
        :rules="rules"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
      >
        <a-form-model-item label="板块" prop="sectionId">
          <a-select
            v-model="form.sectionId"
            placeholder="请选择板块"
            :disabled="isView"
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          >
            <a-select-option v-for="item in sectionList" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="活动类型" prop="typeId">
          <a-select
            v-model="form.typeId"
            placeholder="请选择活动类型"
            :disabled="isView"
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          >
            <a-select-option v-for="item in activityTypeList" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="名称" prop="name">
          <a-input
            v-model.trim="form.name"
            placeholder="请输入名称"
            :maxLength="50"
            :disabled="isView"
          />
        </a-form-model-item>
        <a-form-model-item label="活动时间" prop="activityTime">
          <a-range-picker
            v-model="form.activityTime"
            :show-time="{ format: 'HH:mm' }"
            format="YYYY-MM-DD HH:mm"
            :placeholder="['开始时间', '结束时间']"
            :disabled="isView"
            :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
            style="width: 100%"
          />
        </a-form-model-item>
        <a-form-model-item label="地点" prop="place">
          <a-input
            v-model.trim="form.place"
            placeholder="请输入地点"
            :maxLength="50"
            :disabled="isView"
          />
        </a-form-model-item>
        <a-form-model-item label="规模人数" prop="capacity" extra="规模人数为报名人数上限，报满即止">
          <a-input-number
            v-model="form.capacity"
            :min="1"
            :max="999999"
            :precision="0"
            placeholder="请输入规模人数"
            :disabled="isView"
            style="width: 200px"
          />
        </a-form-model-item>
        <a-form-model-item label="简介" prop="introduction">
          <a-textarea
            v-model.trim="form.introduction"
            placeholder="请输入简介"
            :rows="4"
            :maxLength="500"
            :disabled="isView"
          />
        </a-form-model-item>
        <a-form-model-item label="议程" prop="agenda">
          <div v-if="!form.agenda.length && isView">--</div>
          <div v-for="(item, index) in form.agenda" :key="index" class="dynamic-row">
            <a-time-picker
              v-model="item.startTime"
              format="HH:mm"
              placeholder="开始时间"
              :disabled="isView"
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              style="width: 130px"
            />
            <span class="row-separator">~</span>
            <a-time-picker
              v-model="item.endTime"
              format="HH:mm"
              placeholder="结束时间"
              :disabled="isView"
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              style="width: 130px"
            />
            <a-input
              v-model.trim="item.theme"
              placeholder="请输入主题"
              :maxLength="50"
              :disabled="isView"
              style="width: 180px; margin-left: 8px"
            />
            <a-textarea
              v-model.trim="item.content"
              placeholder="议程内容（可空，可换行）"
              :maxLength="500"
              :disabled="isView"
              :autoSize="{ minRows: 1, maxRows: 4 }"
              class="row-content"
            />
            <a-button
              v-if="!isView"
              type="link"
              icon="delete"
              class="row-delete"
              @click="removeAgenda(index)"
            />
          </div>
          <a-button v-if="!isView" type="dashed" block icon="plus" @click="addAgenda">
            添加议程
          </a-button>
        </a-form-model-item>
        <a-form-model-item label="是否审核" prop="needAudit" :required="true">
          <a-radio-group v-model="form.needAudit" :disabled="isView">
            <a-radio :value="1">是</a-radio>
            <a-radio :value="0">否</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="是否签到" prop="needCheckin" :required="true">
          <a-radio-group v-model="form.needCheckin" :disabled="isView">
            <a-radio :value="1">是</a-radio>
            <a-radio :value="0">否</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="是否开放报名" prop="needRegistration" :required="true">
          <a-radio-group v-model="form.needRegistration" :disabled="isView">
            <a-radio :value="1">是</a-radio>
            <a-radio :value="0">否</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item
          v-if="form.needRegistration === 1"
          label="报名时间"
          extra="不填=不限（默认按活动时间兜底）；配置任一端后，用户端报名仅在该时间窗口内可报"
        >
          <a-date-picker
            v-model="form.signupBeginTime"
            :show-time="{ format: 'HH:mm' }"
            format="YYYY-MM-DD HH:mm"
            placeholder="报名开始时间"
            :disabled="isView"
            :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
            style="width: 240px"
          />
          <span class="row-separator">~</span>
          <a-date-picker
            v-model="form.signupEndTime"
            :show-time="{ format: 'HH:mm' }"
            format="YYYY-MM-DD HH:mm"
            placeholder="报名结束时间"
            :disabled="isView"
            :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
            style="width: 240px"
          />
        </a-form-model-item>
        <a-form-model-item
          v-if="form.needRegistration === 1"
          label="邀请人员"
          extra="非必填，配置后仅这些手机号可对本次活动发起邀约"
        >
          <div v-if="!form.invitePhoneList.length && isView">--</div>
          <div v-for="(phone, index) in form.invitePhoneList" :key="index" class="dynamic-row">
            <a-input
              v-model.trim="form.invitePhoneList[index]"
              placeholder="请输入邀请人员手机号"
              :maxLength="11"
              :disabled="isView"
              style="width: 220px"
            />
            <a-button
              v-if="!isView"
              type="link"
              icon="delete"
              class="row-delete"
              @click="removeInvitePhone(index)"
            />
          </div>
          <a-button v-if="!isView" type="dashed" block icon="plus" @click="addInvitePhone">
            添加邀请人
          </a-button>
        </a-form-model-item>
        <a-form-model-item
          v-if="form.needCheckin === 1"
          label="核销人"
          prop="verifierPhones"
        >
          <div v-if="!form.verifierPhones.length && isView">--</div>
          <div v-for="(phone, index) in form.verifierPhones" :key="index" class="dynamic-row">
            <a-input
              v-model.trim="form.verifierPhones[index]"
              placeholder="请输入核销人手机号"
              :maxLength="11"
              :disabled="isView"
              style="width: 220px"
            />
            <a-button
              v-if="!isView"
              type="link"
              icon="delete"
              class="row-delete"
              @click="removeVerifier(index)"
            />
          </div>
          <a-button v-if="!isView" type="dashed" block icon="plus" @click="addVerifier">
            添加核销人
          </a-button>
        </a-form-model-item>
      </a-form-model>
    </a-spin>
  </a-modal>
</template>

<style scoped lang="scss">
.dynamic-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  .row-separator {
    margin: 0 8px;
    color: #999;
  }

  // 日程内容与时间同行的小文本域，输入换行后自动增高（最多 4 行）
  .row-content {
    flex: 1;
    margin-left: 8px;
  }

  .row-delete {
    padding: 0;
    margin-left: 8px;
  }
}
</style>
